import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Checkbox,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Download, ExpandMore, SwapHoriz } from '@mui/icons-material';
import { stockTransferAPI, lookupAPI } from '../services/api';
import { Store } from '../types';

interface Candidate {
  manufacturer_id: number;
  medicine_name: string;
  manufacturer_name: string;
  pack_size: string;
  from_store_id: number;
  from_store_name: string;
  to_store_id: number;
  to_store_name: string;
  transfer_qty: number;
  landing_price: number;
  line_value: number;
  source_stock: number;
  source_velocity_90d: number;
  source_days_of_cover: number | null;
  dest_stock: number;
  dest_velocity_90d: number;
  dest_days_of_cover: number | null;
  reason: string;
}

interface StorePairGroup {
  from_store_id: number;
  from_store_name: string;
  to_store_id: number;
  to_store_name: string;
  items: Candidate[];
  total_value: number;
}

interface RecommendationsResponse {
  candidates: Candidate[];
  by_store_pair: StorePairGroup[];
}

interface TransferListRow {
  id: number;
  transfer_number: string;
  from_store_id: number;
  from_store_name: string;
  to_store_id: number;
  to_store_name: string;
  status: string;
  total_items: number;
  total_value: number;
  transfer_date: string;
  created_by: string;
}

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  approved: 'info',
  in_transit: 'warning',
  received: 'success',
  cancelled: 'error',
};

const NEXT_STATUS: Record<string, { label: string; status: string } | null> = {
  draft: { label: 'Approve', status: 'approved' },
  approved: { label: 'Mark In-Transit', status: 'in_transit' },
  in_transit: { label: 'Receive', status: 'received' },
  received: null,
  cancelled: null,
};

const inr = (n: number) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const daysLabel = (d: number | null) => (d == null || !Number.isFinite(d) ? '∞' : d.toFixed(1));

const StockTransfers: React.FC<{ selectedStore: Store | null }> = () => {
  const [tab, setTab] = useState(0);
  const [stores, setStores] = useState<Store[]>([]);

  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationsResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creatingPair, setCreatingPair] = useState<string | null>(null);

  const [transfers, setTransfers] = useState<TransferListRow[]>([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => setStores([]));
  }, []);

  const candidateKey = (c: Candidate) => `${c.from_store_id}-${c.to_store_id}-${c.manufacturer_id}`;

  const loadRecommendations = useCallback(async () => {
    setRecLoading(true);
    setRecError(null);
    try {
      const data = await stockTransferAPI.getRecommendations();
      setRecommendations(data);
      setSelected(new Set(data.candidates.map((c: Candidate) => candidateKey(c))));
    } catch (e) {
      setRecError('Failed to load recommendations. Please try again.');
    } finally {
      setRecLoading(false);
    }
  }, []);

  const loadTransfers = useCallback(async () => {
    setTransfersLoading(true);
    try {
      const filters = statusFilter !== 'all' ? { status: statusFilter } : undefined;
      const rows = await stockTransferAPI.list(filters);
      setTransfers(rows);
    } catch (e) {
      // keep prior list on failure
    } finally {
      setTransfersLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (tab === 0 && !recommendations && !recLoading) loadRecommendations();
    if (tab === 1) loadTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, statusFilter]);

  const toggleSelected = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleCreateTransfer = async (group: StorePairGroup) => {
    const items = group.items.filter(c => selected.has(candidateKey(c)));
    if (items.length === 0) return;
    const pairKey = `${group.from_store_id}->${group.to_store_id}`;
    setCreatingPair(pairKey);
    setActionError(null);
    try {
      await stockTransferAPI.create({
        from_store_id: group.from_store_id,
        to_store_id: group.to_store_id,
        items: items.map(c => ({
          manufacturer_id: c.manufacturer_id,
          suggested_qty: c.transfer_qty,
          landing_price: c.landing_price,
          source_stock: c.source_stock,
          source_velocity_90d: c.source_velocity_90d,
          dest_stock: c.dest_stock,
          dest_velocity_90d: c.dest_velocity_90d,
          reason: c.reason,
        })),
      });
      // Drop the created lines from the recommendation view and refresh the list tab.
      setRecommendations(prev => {
        if (!prev) return prev;
        const createdKeys = new Set(items.map(candidateKey));
        const remaining = prev.candidates.filter(c => !createdKeys.has(candidateKey(c)));
        const byPair = new Map<string, StorePairGroup>();
        for (const c of remaining) {
          const key = `${c.from_store_id}->${c.to_store_id}`;
          if (!byPair.has(key)) {
            byPair.set(key, {
              from_store_id: c.from_store_id, from_store_name: c.from_store_name,
              to_store_id: c.to_store_id, to_store_name: c.to_store_name,
              items: [], total_value: 0,
            });
          }
          const g = byPair.get(key)!;
          g.items.push(c);
          g.total_value += c.line_value;
        }
        return { candidates: remaining, by_store_pair: Array.from(byPair.values()).sort((a, b) => b.total_value - a.total_value) };
      });
      setTab(1);
      loadTransfers();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || 'Failed to create transfer.');
    } finally {
      setCreatingPair(null);
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    setActionError(null);
    try {
      await stockTransferAPI.updateStatus(id, status);
      loadTransfers();
    } catch (e: any) {
      setActionError(e?.response?.data?.error || 'Failed to update status.');
    }
  };

  const handleDownloadPicklist = async (t: TransferListRow) => {
    try {
      const blob = await stockTransferAPI.downloadPicklistCsv(t.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${t.transfer_number}_picklist.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setActionError('Failed to download picklist.');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Detects slow-moving stock at one store (over 45 days of cover) that's fast-moving with
        low stock at another store, and suggests a transfer instead of a fresh purchase order.
        Review the suggested lines, adjust selection, then create a transfer and walk it through
        Approve → In-Transit → Receive as the physical move happens.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Recommendations" />
        <Tab label="Transfers" />
      </Tabs>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button size="small" onClick={loadRecommendations} disabled={recLoading}>
              {recLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Box>
          {recError && <Alert severity="error" sx={{ mb: 2 }}>{recError}</Alert>}
          {recLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
          {!recLoading && recommendations && recommendations.by_store_pair.length === 0 && (
            <Alert severity="info">No transfer opportunities found right now.</Alert>
          )}
          {!recLoading && recommendations?.by_store_pair.map(group => {
            const pairKey = `${group.from_store_id}->${group.to_store_id}`;
            const selectedCount = group.items.filter(c => selected.has(candidateKey(c))).length;
            return (
              <Accordion key={pairKey} defaultExpanded={false} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <SwapHoriz fontSize="small" />
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.from_store_name} → {group.to_store_name}
                    </Typography>
                    <Chip size="small" label={`${group.items.length} items`} />
                    <Chip size="small" color="primary" label={inr(group.total_value)} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={selectedCount === 0 || creatingPair === pairKey}
                      onClick={() => handleCreateTransfer(group)}
                    >
                      {creatingPair === pairKey ? 'Creating…' : `Create Transfer (${selectedCount})`}
                    </Button>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell><strong>Medicine</strong></TableCell>
                          <TableCell align="right"><strong>Source Stock</strong></TableCell>
                          <TableCell align="right"><strong>Source Days Cover</strong></TableCell>
                          <TableCell align="right"><strong>Dest Stock</strong></TableCell>
                          <TableCell align="right"><strong>Dest Days Cover</strong></TableCell>
                          <TableCell align="right"><strong>Qty</strong></TableCell>
                          <TableCell align="right"><strong>Value</strong></TableCell>
                          <TableCell><strong>Reason</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.items.map(c => {
                          const key = candidateKey(c);
                          return (
                            <TableRow key={key}>
                              <TableCell padding="checkbox">
                                <Checkbox size="small" checked={selected.has(key)} onChange={() => toggleSelected(key)} />
                              </TableCell>
                              <TableCell>
                                {c.medicine_name}
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {c.manufacturer_name} · {c.pack_size}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{c.source_stock}</TableCell>
                              <TableCell align="right">{daysLabel(c.source_days_of_cover)}</TableCell>
                              <TableCell align="right">{c.dest_stock}</TableCell>
                              <TableCell align="right">{daysLabel(c.dest_days_of_cover)}</TableCell>
                              <TableCell align="right">{c.transfer_qty}</TableCell>
                              <TableCell align="right">{inr(c.line_value)}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{c.reason}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              select size="small" label="Status" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="in_transit">In Transit</MenuItem>
              <MenuItem value="received">Received</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
            <Button size="small" onClick={loadTransfers} disabled={transfersLoading}>
              {transfersLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Box>

          {transfersLoading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

          {!transfersLoading && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Transfer #</strong></TableCell>
                    <TableCell><strong>From → To</strong></TableCell>
                    <TableCell align="right"><strong>Items</strong></TableCell>
                    <TableCell align="right"><strong>Value</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transfers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                        No transfers found.
                      </TableCell>
                    </TableRow>
                  )}
                  {transfers.map(t => {
                    const next = NEXT_STATUS[t.status];
                    return (
                      <TableRow key={t.id}>
                        <TableCell>{t.transfer_number}</TableCell>
                        <TableCell>{t.from_store_name} → {t.to_store_name}</TableCell>
                        <TableCell align="right">{t.total_items}</TableCell>
                        <TableCell align="right">{inr(t.total_value)}</TableCell>
                        <TableCell>{new Date(t.transfer_date).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" color={STATUS_COLORS[t.status] || 'default'} label={t.status} />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {next && (
                              <Button size="small" variant="outlined" onClick={() => handleStatusChange(t.id, next.status)}>
                                {next.label}
                              </Button>
                            )}
                            {(t.status === 'draft' || t.status === 'approved') && (
                              <Button size="small" color="error" onClick={() => handleStatusChange(t.id, 'cancelled')}>
                                Cancel
                              </Button>
                            )}
                            <Button size="small" startIcon={<Download />} onClick={() => handleDownloadPicklist(t)}>
                              CSV
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StockTransfers;
